/**
 * Document Knowledge schema — the contract every definition must satisfy.
 *
 * The schema is data too: CAPABILITIES drives both validation and the UI's
 * "Supports" chips, so adding a capability means adding one line here — no
 * component changes. Definitions describe WHAT a document is, never how it
 * looks or renders.
 */

/** Capability flags, with the label the UI shows when the flag is true. */
export const CAPABILITIES = [
  { key: 'supportsMultiplePages', label: 'Multiple Pages' },
  { key: 'supportsBleed', label: 'Bleed' },
  { key: 'supportsImages', label: 'Images' },
  { key: 'supportsIcons', label: 'Icons' },
  { key: 'supportsQR', label: 'QR' },
  { key: 'supportsTables', label: 'Tables' },
  { key: 'supportsPricing', label: 'Pricing' },
  { key: 'supportsCTA', label: 'CTA' },
  { key: 'supportsOffers', label: 'Offers' },
  { key: 'supportsTimeline', label: 'Timeline' },
  { key: 'supportsGallery', label: 'Gallery' },
];

export const ORIENTATIONS = ['portrait', 'landscape', 'square'];

const STRING_FIELDS = ['id', 'name', 'description', 'purpose'];

/**
 * Validate a definition object. Returns { valid, errors[] } — used by tests
 * and by the registry at module load, so a malformed definition fails fast
 * in development instead of rendering a broken panel.
 */
export const validateDefinition = (def) => {
  const errors = [];
  if (!def || typeof def !== 'object') {
    return { valid: false, errors: ['definition must be an object'] };
  }

  STRING_FIELDS.forEach((field) => {
    if (typeof def[field] !== 'string' || !def[field].trim()) {
      errors.push(`"${field}" must be a non-empty string`);
    }
  });

  if (!Array.isArray(def.recommendedSizes) || def.recommendedSizes.length === 0) {
    errors.push('"recommendedSizes" must be a non-empty array');
  } else {
    def.recommendedSizes.forEach((size, i) => {
      if (!size?.name || !Number.isFinite(size.width) || !Number.isFinite(size.height)) {
        errors.push(`recommendedSizes[${i}] needs { name, width, height }`);
      }
    });
  }

  if (!Array.isArray(def.supportedOrientations) || def.supportedOrientations.length === 0) {
    errors.push('"supportedOrientations" must be a non-empty array');
  } else {
    def.supportedOrientations.forEach((o) => {
      if (!ORIENTATIONS.includes(o)) errors.push(`unknown orientation "${o}"`);
    });
  }

  CAPABILITIES.forEach(({ key }) => {
    if (typeof def[key] !== 'boolean') errors.push(`"${key}" must be a boolean`);
  });

  if (!Array.isArray(def.typicalSections) || def.typicalSections.length === 0) {
    errors.push('"typicalSections" must be a non-empty array');
  }

  return { valid: errors.length === 0, errors };
};
